import { useLocation } from "wouter";
import { ChevronDown } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useI18n } from "@/hooks/use-i18n";
import { track } from "@/lib/analytics";
import { EVENTS } from "@/lib/analytics-events";

import { LANGUAGES } from "./nav-data";

// Port 1:1 del language menu desktop da ChakraHeader:384-481.
// Header rosso → trigger text-white; dropdown su bianco → token Fase 0
// (bg-surface-subtle per attivo/hover, text-ink, text-brand-700 era blue.50/gray.800).
export function LanguageSwitcher() {
  const { language } = useI18n();
  const [location, navigate] = useLocation();

  // Port 1:1 di handleLanguageChange da ChakraHeader:45-67.
  const handleLanguageChange = (newLang: "it" | "en" | "de" | "sl") => {
    // Evita cambi multipli rapidi
    if (newLang === language) return;

    const currentPath = location;

    // Rimuovi eventuale prefisso lingua esistente
    const pathWithoutLang = currentPath.replace(/^\/(it|en|de|sl)/, "") || "/";

    // Costruisci la nuova URL con il nuovo prefisso lingua
    const newUrl = `/${newLang}${pathWithoutLang}`;

    // Imposta un flag temporaneo per evitare loop
    (window as any).__languageChanging = true;

    track(EVENTS.LANGUAGE_CHANGE, { lang_from: language, lang_to: newLang });

    // Naviga alla nuova URL (questo causerà un cambio di lingua automatico via I18nProvider)
    navigate(newUrl);

    // Rimuovi il flag dopo un breve delay
    setTimeout(() => {
      (window as any).__languageChanging = false;
    }, 100);
  };

  const current = LANGUAGES.find((lang) => lang.code === language);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={current?.label}
          className="flex h-11 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-white/90 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 data-[state=open]:bg-white/10"
        >
          <span className="inline-flex h-5 w-5 items-center" aria-hidden="true">{current?.flag}</span>
          {/* "DE/AT" solo da tablet: su telefono spingerebbe fuori il pulsante del menu */}
          {language === "de" ? (
            <>
              <span className="md:hidden">DE</span>
              <span className="hidden md:inline">DE/AT</span>
            </>
          ) : language.toUpperCase()}
          <ChevronDown className="h-[13px] w-[13px] opacity-80" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={4} className="z-[10000] min-w-[200px] rounded-lg border border-line-subtle bg-white p-1.5 text-ink shadow-overlay">
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`min-h-[40px] rounded-md focus:bg-page ${
              language === lang.code ? "bg-surface-subtle font-medium text-action-hover focus:bg-surface-subtle" : "font-normal"
            }`}
          >
            <span className="mr-2 inline-flex h-5 w-5 items-center">{lang.flag}</span>
            <span className="text-sm">
              {lang.label}
              <span className="ml-2 text-xs font-normal text-ink-muted">{lang.display}</span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
